import React, { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate, useParams, useLocation } from "react-router-dom";
import { Button, Popconfirm, Space, Tabs, Typography, Tag, message } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
  readSequence,
  activateSequence,
  pauseSequence,
  deleteSequence,
} from "../../../redux/actions/sequencesAction";
import { SequenceDetailContext } from "./SequenceDetailContext";
import { isValidSequenceId, sequenceDetailPath } from "./sequenceUtils";

const { Title, Text } = Typography;

const TAB_SECTIONS = ["editor", "enrollments", "report"];

const STATUS_COLORS = {
  draft: "default",
  active: "green",
  paused: "orange",
  archived: "default",
};

function sectionFromPathname(pathname, id) {
  if (id === "new") return "editor";
  const prefix = `/app/sequences/${id}`;
  if (pathname === prefix || pathname === `${prefix}/`) return "editor";
  for (const section of TAB_SECTIONS) {
    if (pathname.endsWith(`/${section}`)) return section;
  }
  return "editor";
}

export default function SequenceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const isNew = id === "new" || !isValidSequenceId(id);

  const [sequenceRecord, setSequenceRecord] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [statusChanging, setStatusChanging] = useState(false);

  const activeSection = isNew ? "editor" : sectionFromPathname(location.pathname, id);

  const reloadSequence = useCallback(() => {
    if (!isValidSequenceId(id)) return Promise.resolve();
    setLoading(true);
    return dispatch(readSequence(id))
      .then((data) => {
        setSequenceRecord(data);
        setLoading(false);
        return data;
      })
      .catch(() => {
        setLoading(false);
        return null;
      });
  }, [dispatch, id]);

  useEffect(() => {
    if (id === "undefined") {
      message.error("Invalid sequence link.");
      navigate("/app/sequences", { replace: true });
      return;
    }
    if (isNew) {
      setSequenceRecord(null);
      setLoading(false);
      if (location.pathname !== "/app/sequences/new") {
        navigate("/app/sequences/new", { replace: true });
      }
      return;
    }
    if (!isValidSequenceId(id)) {
      navigate("/app/sequences", { replace: true });
      return;
    }
    reloadSequence();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const onTabChange = (key) => {
    navigate(sequenceDetailPath(id, key));
  };

  const toggleStatus = async () => {
    setStatusChanging(true);
    try {
      if (sequenceRecord.status === "active") {
        await dispatch(pauseSequence(id));
        message.success("Sequence paused");
      } else {
        await dispatch(activateSequence(id));
        message.success("Sequence activated");
      }
      reloadSequence();
    } catch (e) {
      message.error("Failed to update sequence status");
    } finally {
      setStatusChanging(false);
    }
  };

  const onDelete = async () => {
    try {
      await dispatch(deleteSequence(id));
      message.success("Sequence deleted");
      navigate("/app/sequences");
    } catch (e) {
      message.error("Failed to delete sequence");
    }
  };

  const tabItems = isNew
    ? [{ key: "editor", label: "Editor" }]
    : [
        { key: "editor", label: "Editor" },
        { key: "enrollments", label: "Enrolments" },
        { key: "report", label: "Report" },
      ];

  const contextValue = {
    id,
    isNew,
    sequenceRecord,
    loading,
    reloadSequence,
  };

  return (
    <SequenceDetailContext.Provider value={contextValue}>
      <div className="SequenceDetail">
        <Space direction="vertical" size="middle" style={{ width: "100%" }}>
          <Space
            align="start"
            style={{ width: "100%", justifyContent: "space-between" }}
            wrap
          >
            <Space align="start">
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate("/app/sequences")}
              />
              <div>
                <Title level={5} style={{ margin: 0 }}>
                  {isNew ? "New sequence" : sequenceRecord?.name || "Sequence"}
                </Title>
                {!isNew && sequenceRecord && (
                  <Space style={{ marginTop: 8 }} wrap>
                    <Tag color={STATUS_COLORS[sequenceRecord.status] || "default"}>
                      {sequenceRecord.status}
                    </Tag>
                    <Text type="secondary">
                      {sequenceRecord.steps?.length || 0} step
                      {sequenceRecord.steps?.length === 1 ? "" : "s"}
                    </Text>
                  </Space>
                )}
              </div>
            </Space>
            {!isNew && sequenceRecord && (
              <Space>
                <Button
                  loading={statusChanging}
                  disabled={sequenceRecord.status === "archived"}
                  onClick={toggleStatus}
                >
                  {sequenceRecord.status === "active" ? "Pause" : "Activate"}
                </Button>
                <Popconfirm
                  title="Delete this sequence?"
                  description="Existing enrolments are kept as history but will stop being processed."
                  onConfirm={onDelete}
                  okText="Delete"
                  okButtonProps={{ danger: true }}
                >
                  <Button danger>Delete</Button>
                </Popconfirm>
              </Space>
            )}
          </Space>

          <Tabs activeKey={activeSection} items={tabItems} onChange={onTabChange} />

          <Outlet />
        </Space>
      </div>
    </SequenceDetailContext.Provider>
  );
}
